import { FC, useState, useEffect } from "react";
import { clientContent } from "../../client/content";
import { withPage } from "../../components/Page";
import { PageHeader } from "../../components/PageHeader";
import { PageToc, TocItem } from "../../components/PageToc";
import { DocumentTable, DocumentRowData } from "../../components/DocumentTable";
import {
  DefinitionList,
  DefinitionGroup,
} from "../../components/DefinitionList";
import { DocumentRecordCard } from "../../components/DocumentRecordCard";
import { InfoCallout } from "../../components/InfoCallout";
import styles from "./MechanicalDesignCriteria.module.scss";

const TOC_ITEMS: TocItem[] = [
  { id: "section-post-fitout", label: "1. Post-fitout information" },
  { id: "section-westco", label: "2.1 Westco ductwork" },
  { id: "section-meit", label: "2.2 MEIT design criteria" },
  { id: "section-dms", label: "2.3 DMS controls" },
  { id: "section-wormald", label: "3.1 Wormald" },
];

const ROWS_2016: DocumentRowData[] = [
  {
    id: "doc-l2-c1",
    source: "Engie DVD",
    description: "Level 2 Core 1, Design Criteria",
    files: [{ name: "Level 2 Core 1 Design Criteria.pdf", type: "PDF" }],
  },
  {
    id: "doc-l3-c1",
    source: "Engie DVD",
    description: "Level 3 Core 1, Design Criteria",
    files: [{ name: "Level 3 Core 1 Design Criteria.pdf", type: "PDF" }],
  },
  {
    id: "doc-l6-c12",
    source: "Engie DVD",
    description: "Level 6 Cores 1 & 2, Design Criteria",
    files: [{ name: "Level 6 Cores 1 & 2 Design Criteria.pdf", type: "PDF" }],
  },
  {
    id: "doc-lg-core",
    source: "Engie DVD",
    description: "Lower Ground Floor Design Criteria",
    files: [{ name: "Lower Ground Floor Design Criteria.pdf", type: "PDF" }],
  },
];

const ROWS_2013: DocumentRowData[] = [
  {
    id: "doc-gf-closeout",
    source: "Coffey CDs",
    description: "Ground Floor Close Out Dec 2012 Design Criteria",
    files: [
      { name: "Grd Flr Close Out Dec 12 Design Criteria.pdf", type: "PDF" },
    ],
  },
  {
    id: "doc-4f-closeout",
    source: "Coffey CDs",
    description: "4th Floor Close Out Dec 2012 Design Criteria",
    files: [
      { name: "4th Flr Close Out Dec 12 Design Criteria.pdf", type: "PDF" },
    ],
  },
];

const ROWS_WESTCO: DocumentRowData[] = [
  {
    id: "doc-westco-1",
    source: "Westco Ductwork",
    description: "Summer, winter and noise criteria.",
    files: [
      { name: "Westco Ductwork Design Criteria.pdf", type: "PDF", href: "/documents/Westco Ductwork Design Criteria.pdf" },
      { name: "Manual Reference 887-11.dwg", type: "DWG" },
    ],
  },
];

const ROWS_WORMALD: DocumentRowData[] = [
  {
    id: "doc-wormald-1",
    source: "Wormald",
    description: "Fitout design criteria MERs",
    files: [{ name: "Hi-Fog descriptions & design criteria.pdf", type: "PDF" }],
  },
  {
    id: "doc-wormald-2",
    source: "Wormald – Marioff",
    description: "Fitout pipework calculations",
    files: [{ name: "Hi-Fog design calculations.pdf", type: "PDF" }],
  },
  {
    id: "doc-wormald-3",
    source: "Wormald",
    description: "Fitout, 9 sprinklers for 30 min",
    files: [{ name: "Hi-Fog sprinkler design.pdf", type: "PDF" }],
  },
];

const MEIT_LEFT_GROUPS: DefinitionGroup[] = [
  {
    title: "External design conditions",
    items: [
      { term: "Summer", detail: "Solar gain 29°C db / 21°C wb" },
      { term: "Winter", detail: "−2°C db / 100% RH" },
      { term: "Trading floors", detail: "22°C ±2°C" },
      { term: "Toilets", detail: "18°C minimum" },
    ],
  },
  {
    title: "Chilled water system capacities",
    items: [
      { term: "Installed capacity", detail: "3 × 1,200 kW chillers" },
      { term: "Predicted peak load", detail: "East 2,934 kW · West 2,901 kW" },
      {
        term: "Actual operational capacity",
        detail: "East 900 kW · West 362.2 kW max",
      },
    ],
  },
];

const MEIT_RIGHT_GROUPS: DefinitionGroup[] = [
  {
    title: "System flow rates",
    items: [
      { term: "Primary circuit", detail: "East / West 150 l/s max" },
      { term: "Secondary circuit", detail: "East 145 l/s max" },
      { term: "SER/MER", detail: "80 l/s max capacity" },
    ],
  },
  {
    title: "Occupant densities",
    narrative:
      "General offices: 1 person per 6.5 to 7.5m². Trading floors: 1 person per 5.5m². The existing assumptions are retained as source information.",
  },
];

const MechanicalDesignCriteria: FC = () => {
  const [activeSection, setActiveSection] = useState("section-post-fitout");

  // Handle active TOC tracking with IntersectionObserver
  useEffect(() => {
    const sectionIds = TOC_ITEMS.map((item) => item.id);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-20% 0px -70% 0px",
      },
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div id="mechanical-design-criteria" className="page-container-right">
      <PageHeader
        eyebrow="DESIGN CRITERIA / MECHANICAL"
        title="Mechanical design criteria"
        subtitle="Base-build, fitout and supporting information for mechanical building systems."
        secondaryAction={{
          label: "Print / export",
          href: "/more-info",
        }}
        primaryAction={{
          label: "Open document library",
          href: "/more-info",
        }}
      />

      <section
        className={styles.introSection}
        aria-label="About this information"
      >
        <div className={styles.aboutCard}>
          <div className={styles.aboutContent}>
            <h2 className={styles.aboutHeading}>About this information</h2>
            <p className={styles.aboutText}>
              This page retains the existing design criteria and source
              documentation, structured so people can scan the important values,
              navigate directly to a section, and open the underlying files in
              context.
            </p>
          </div>
          <div className={styles.statsBar}>
            <div className={styles.statBlock}>
              <span className={styles.statLabel}>Source documents</span>
              <span className={styles.statValue}>18 records</span>
            </div>
            <div className={styles.statBlock}>
              <span className={styles.statLabel}>Sections</span>
              <span className={styles.statValue}>7 topics</span>
            </div>
            <div className={styles.statBlock}>
              <span className={styles.statLabel}>Latest source</span>
              <span className={styles.statValue}>2016</span>
            </div>
          </div>
        </div>

        <InfoCallout variant="status" title="Information status">
          Compiled from base-build and fitout records. Each linked document
          retains its original source, file type and issue information.
        </InfoCallout>
      </section>

      <div className={styles.layoutGrid}>
        <aside aria-label="Table of contents" className={styles.tocAside}>
          <PageToc
            items={TOC_ITEMS}
            activeId={activeSection}
            onItemClick={(id) => setActiveSection(id)}
          />
        </aside>

        <main className={styles.contentColumn}>
          {/* Section 1 */}
          <article
            id="section-post-fitout"
            className={styles.sectionCard}
            aria-labelledby="heading-post-fitout"
          >
            <div className={styles.sectionHeader}>
              <h2 id="heading-post-fitout" className={styles.sectionTitle}>
                1. Post-fitout information
              </h2>
              <p className={styles.sectionIntro}>
                Historic documents grouped by issue year. The records remain
                available exactly as source material, while the table makes them
                easier to find and compare.
              </p>
            </div>

            <DocumentTable
              showFilterBar
              filterPlaceholder="Filter files in this section"
              resultCountText="9 documents"
              subheading="2016"
              columnLayout="separateType"
              columns={["SOURCE", "DESCRIPTION", "FILE", "TYPE"]}
              rows={ROWS_2016}
              ariaLabel="2016 Post-fitout records"
            />

            <DocumentTable
              subheading="2013"
              columnLayout="separateType"
              columns={["SOURCE", "DESCRIPTION", "FILE", "TYPE"]}
              rows={ROWS_2013}
              ariaLabel="2013 Post-fitout records"
            />
          </article>

          {/* Section 2 */}
          <article
            id="section-westco"
            className={styles.sectionCard}
            aria-labelledby="heading-westco"
          >
            <div className={styles.sectionHeader}>
              <h2 id="heading-westco" className={styles.sectionTitle}>
                2.1 Westco ductwork
              </h2>
              <p className={styles.sectionIntro}>
                Supporting ductwork criteria and associated reference material.
              </p>
            </div>

            <DocumentTable
              columnLayout="inlineBadge"
              columns={["SOURCE", "DESCRIPTION", "SUPPORTING FILES"]}
              rows={ROWS_WESTCO}
              ariaLabel="Westco ductwork criteria"
            />

            <InfoCallout
              variant="why"
              title="Why links and file types are surfaced"
            >
              Users can distinguish the original record from the supporting
              drawing before opening it, without altering or summarising what
              the source represents.
            </InfoCallout>
          </article>

          {/* Section 3 */}
          <article
            id="section-meit"
            className={styles.sectionCard}
            aria-labelledby="heading-meit"
          >
            <div className={styles.sectionHeader}>
              <h2 id="heading-meit" className={styles.sectionTitle}>
                2.2 MEIT design criteria
              </h2>
              <p className={styles.sectionIntro}>
                Existing technical text is presented as readable structured
                information; individual source documents remain immediately
                available below.
              </p>
            </div>

            <DefinitionList
              leftGroups={MEIT_LEFT_GROUPS}
              rightGroups={MEIT_RIGHT_GROUPS}
            />

            <InfoCallout variant="note">
              <strong>Reading long-form criteria:</strong> key values appear in
              scannable pairs; explanatory narrative remains directly
              underneath, without being reduced to an untraceable summary.
            </InfoCallout>

            <h3 className={styles.relatedHeading}>Related source documents</h3>
            <div className={styles.relatedList}>
              <DocumentRecordCard
                badge="PDF"
                title="MEIT Design Criteria"
                subtitle="Design criteria and outline scope of works · Rev 04 · 2016"
                actionText="Open in Pulse →"
                href="/more-info"
              />
              <DocumentRecordCard
                badge="PDF"
                title="Concept design MEIT"
                subtitle="Conceptual design report · DrKW"
                actionText="Open in Pulse →"
                href="/more-info"
              />
            </div>
          </article>

          {/* Section 4 */}
          <article
            id="section-dms"
            className={styles.sectionCard}
            aria-labelledby="heading-dms"
          >
            <div className={styles.sectionHeader}>
              <h2 id="heading-dms" className={styles.sectionTitle}>
                2.3 DMS controls
              </h2>
              <p className={styles.sectionIntro}>
                Controls information remains a detailed informational record—not
                a live controls or CAFM interface.
              </p>
            </div>

            <p className={styles.bodyParagraph}>
              The building comprises ten storeys for client occupation. The
              general use of each floor is detailed in the relevant
              documentation. The controls system is described as a resilient
              solution that is straightforward to operate while avoiding
              unnecessary single points of failure.
            </p>
            <p className={styles.bodyParagraph}>
              Existing controls associated with the base-build system are
              recorded alongside the subsequent fitout work. The page preserves
              the supporting explanation and identifies linked documents, so the
              reader can understand both the design intent and the relevant
              source material.
            </p>
          </article>

          {/* Section 5 */}
          <article
            id="section-wormald"
            className={styles.sectionCard}
            aria-labelledby="heading-wormald"
          >
            <div className={styles.sectionHeader}>
              <h2 id="heading-wormald" className={styles.sectionTitle}>
                3.1 Wormald
              </h2>
              <p className={styles.sectionIntro}>
                Fire protection criteria and supporting documents.
              </p>
            </div>

            <DocumentTable
              columnLayout="inlineBadge"
              columns={["SOURCE", "DESCRIPTION", "FILE"]}
              rows={ROWS_WORMALD}
              ariaLabel="Wormald fire protection criteria"
            />
          </article>

          <footer className={styles.pageFooter}>
            End of current page content ·{" "}
            <a href="#mechanical-design-criteria">Back to top</a>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default withPage(MechanicalDesignCriteria)({
  meta: {
    title: "Mechanical design criteria | Meridian Tower | Pulse",
    description:
      "Base-build, fitout and supporting information for mechanical building systems. Historic documents, ductwork, MEIT criteria, DMS controls and Wormald.",
  },
  breadcrumbs: [
    { label: clientContent.systemName, href: "/" },
    { label: clientContent.clientName },
    { label: "Design criteria" },
  ],
  searchPlaceholder: "Search all information",
});
